/**
 * <estimated-delivery> Web Component
 *
 * Fetches and displays an estimated ship-by date from the JY API.
 * Falls back to static client-side calculation on API error.
 *
 * Attributes:
 *   data-type       "ecom" (Ready to Ship / E-commerce) or "mto" (Made to Order)
 *   data-lead-weeks Number of weeks lead time (MTO only)
 *   data-rts-days   Fallback business days for ECOM (default: 5)
 *
 * Usage:
 *   <estimated-delivery data-type="ecom"></estimated-delivery>
 *   <estimated-delivery data-type="mto" data-lead-weeks="12"></estimated-delivery>
 */

import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/** @type {Map<string, Promise<string|null>>} */
const esdCache = new Map();

/**
 * Fetch estimated ship date from API. Returns formatted date string or null on failure.
 * @param {'ecom'|'mto'} type
 * @param {number} [leadWeeks]
 * @returns {Promise<string|null>}
 */
function fetchEsd(type, leadWeeks = 12) {
  const url =
    type === 'mto'
      ? `/apps/jyapi/retail/esd/${leadWeeks}`
      : '/apps/jyapi/retail/esd-ecom';

  const cacheKey = type === 'mto' ? `mto-${leadWeeks}` : 'ecom';

  if (esdCache.has(cacheKey)) return /** @type {Promise<string|null>} */ (esdCache.get(cacheKey));

  const promise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`ESD API ${res.status}`);
      return res.json();
    })
    .then((json) => {
      if (json?.data?.esd) return json.data.esd;
      throw new Error('Missing esd field');
    })
    .catch((err) => {
      console.warn('[estimated-delivery] API error, using fallback:', err);
      esdCache.delete(cacheKey);
      return null;
    });

  esdCache.set(cacheKey, promise);
  return promise;
}

class EstimatedDelivery extends HTMLElement {
  static RTS_DEFAULT_DAYS = 2;

  /** @type {number} */
  _renderVersion = 0;

  /** @type {boolean} */
  _renderPending = false;

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.render();

    // Recreate the controller on every (re)connect. The element can be
    // disconnected then reconnected by morph; reusing an aborted signal
    // would silently drop the new listener.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Scope to the closest section so this instance only reacts to variant
    // updates dispatched from within its own section — keeps the cart's
    // estimated-delivery from reacting to PDP variant changes. Cross-product
    // (combined-listing) updates are dispatched on document and morph the
    // server-rendered element directly, so no listener is needed for that case.
    const target = this.closest('.shopify-section') ?? this;
    target.addEventListener(
      ThemeEvents.variantUpdate,
      /** @type {EventListener} */(this.#handleVariantUpdate),
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * Handles a variant update event.
   *
   * @param {VariantUpdateEvent} e - The variant update event.
   */
  #handleVariantUpdate = (e) => {
    if (this.dataset.type !== 'ecom') return;
    const shippingDate = /** @type {HTMLElement|null} */ (this.closest('.shipping-date'));
    if (!shippingDate) return;
    const variant = e.detail?.resource;
    shippingDate.hidden = !variant?.available;
  };

  static get observedAttributes() {
    return ['data-type', 'data-lead-weeks', 'data-rts-days', 'rendered'];
  }

  /** @param {string} name */
  attributeChangedCallback(name) {
    if (!this.isConnected) return;
    // When morph replaces the section, it copies attributes from the server HTML
    // (which never has 'rendered') onto the live element, removing 'rendered'.
    // This triggers a re-render so the client-side content is restored.
    // Skip when 'rendered' is being added (we set it ourselves in _setDate).
    if (name === 'rendered' && this.hasAttribute('rendered')) return;
    // Debounce: when morphing changes multiple attributes in the same tick,
    // wait for all to settle before rendering with final values.
    if (this._renderPending) return;
    this._renderPending = true;
    queueMicrotask(() => {
      this._renderPending = false;
      this.render();
    });
  }

  /**
   * Add business days (skip weekends) to a date.
   * @param {Date} start
   * @param {number} days
   * @returns {Date}
   */
  addBusinessDays(start, days) {
    const result = new Date(start);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return result;
  }

  /**
   * Add calendar days.
   * @param {Date} start
   * @param {number} days
   * @returns {Date}
   */
  addCalendarDays(start, days) {
    const result = new Date(start);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Format date as "Day, Mon DD" (e.g., "Mon, Sep 1")
   * @param {Date} date
   * @returns {string}
   */
  formatDate(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  }

  /**
   * Parse an ISO date string (YYYY-MM-DD) and format it.
   * @param {string} isoDate
   * @returns {string}
   */
  formatIsoDate(isoDate) {
    // Parse as local date (not UTC) by splitting to avoid timezone offset issues
    const parts = isoDate.split('-').map(Number);
    const date = new Date(parts[0] || 0, (parts[1] || 1) - 1, parts[2] || 1);
    return this.formatDate(date);
  }

  /**
   * Compute the estimated delivery date (fallback when API fails).
   * @returns {Date}
   */
  computeFallbackDate() {
    const type = this.dataset.type ?? 'ecom';
    const now = new Date();

    if (type === 'mto') {
      const leadWeeks = parseInt(this.dataset.leadWeeks ?? '12', 10) || 12;
      return this.addCalendarDays(now, leadWeeks * 7);
    }

    // ECOM: add business days
    const rtsDays = this.dataset.rtsDays ? parseInt(this.dataset.rtsDays, 10) : EstimatedDelivery.RTS_DEFAULT_DAYS;
    const etHour = parseInt(now.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', hour12: false }), 10);
    const totalDays = rtsDays + (etHour >= 14 ? 1 : 0);
    return this.addBusinessDays(now, totalDays);
  }

  /**
   * @param {string} formatted
   */
  _setDate(formatted) {
    const dateEl = this.querySelector('.estimated-delivery__date');
    if (dateEl) {
      dateEl.textContent = formatted;
    } else {
      const learMoreLink = '<a href="/pages/contact-us" target="_blank">Learn More.</a>';
      const type = this.dataset.type ?? 'ecom';
      const tooltipText =
        type === 'mto'
          ? `This date reflects the delivery timeline selected. Shipping method is chosen at checkout. Made-to-Order items are final sale. ${learMoreLink}`
          : `This date reflects when your order will depart our warehouse. Ready-to-Ship items typically ship within 2-3 business days. Select your shipping method at checkout. ${learMoreLink}`;

      this.innerHTML = /* html */ `
        <div class="estimated-delivery__content"><span class="estimated-delivery__label">Estimated Ship Date </span><span class="estimated-delivery__date">${formatted}</span></div>
        <div class="estimated-delivery__icon shipping-date__tooltip">
          <div class="tooltip-trigger">
            <div class="svg-wrapper">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1"/>
                <path d="M8 4.5V8.5" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
                <circle cx="8" cy="11" r="0.5" fill="currentColor"/>
              </svg>
            </div>
            <div class="tooltip" role="tooltip">
              <div class="tooltip__content">
                <div class="tooltip__arrow"></div>
                <div class="tooltip__text">${tooltipText}</div>
              </div>
            </div>
          </div>
        </div>
      `;
      this.setAttribute('rendered', '');
    }
  }

  async render() {
    const version = ++this._renderVersion;
    const type = /** @type {'ecom'|'mto'} */ (this.dataset.type ?? 'ecom');
    const leadWeeks = type === 'mto' ? parseInt(this.dataset.leadWeeks ?? '12', 10) || 12 : 12;

    // Render fallback immediately
    this._setDate(this.formatDate(this.computeFallbackDate()));

    const apiDate = await fetchEsd(type, leadWeeks);

    // Guard: if a newer render started while we awaited, discard this result.
    if (version !== this._renderVersion) return;

    if (apiDate) {
      this._setDate(this.formatIsoDate(apiDate));
    }
  }
}

if (!customElements.get('estimated-delivery')) {
  customElements.define('estimated-delivery', EstimatedDelivery);
}
